import { useFormContext, useWatch } from 'react-hook-form';
import FormCheckbox from 'components/common/forms/FormCheckbox';
import SymptomTable from 'components/pages/chart/dentalHistoryChart/SymptomTable';
import FormRadioBtn from 'components/common/forms/FormRadioBtn';
import FormAutoHeightTextarea from 'components/common/forms/FormAutoHeightTextarea';
import styles from 'styles/pages/chart/DentalHistoryChart.module.scss';
import { STYLES_VARIABLES } from 'constants/styleVariables';
import {
  CheckPoint,
  CheckPointLabel,
  DentalHistoryChartFields,
  DentalHistoryChartRadioBtn,
  DentalHistoryChartRadioBtnLabel,
  DentalProblem,
  DentalProblemLabel,
  DentalTreatment,
  DentalTreatmentLabel,
  LastScalingList,
} from 'constants/chart/dentalHistoryChart';
import useModal from 'hooks/useModal';

const numberStyle = {
  backgroundColor: STYLES_VARIABLES.GRAY_01,
  color: STYLES_VARIABLES.GRAY_07,
  fontSize: '24px',
  fontWeight: '700',
  width: '64px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  borderTopLeftRadius: '8px',
  borderBottomLeftRadius: '8px',
};

type TProps = {
  isRegister: boolean;
  isReadOnly: boolean;
};

const DentalHistoryChart = ({ isRegister, isReadOnly }: TProps) => {
  const { openModal } = useModal();

  const { setValue, control } = useFormContext();

  const checkPointWatch = useWatch({
    name: DentalHistoryChartFields.CheckPoint,
    control,
  });
  const checkPointDetailWatch = useWatch({
    name: DentalHistoryChartFields.CheckPointDetail,
    control,
  });

  const badExperienceWatch = useWatch({
    name: DentalHistoryChartFields.BadExperience,
    control,
  });
  const badExperienceDetailWatch = useWatch({
    name: DentalHistoryChartFields.BadExperienceDetail,
    control,
  });

  const dentalTreatmentWatch = useWatch({
    name: DentalHistoryChartFields.DentalTreatment,
    control,
  });
  const dentalTreatmentDetailWatch = useWatch({
    name: DentalHistoryChartFields.DentalTreatmentDetail,
    control,
  });

  const dentalProblemWatch = useWatch({
    name: DentalHistoryChartFields.DentalProblem,
    control,
  });
  const dentalProblemDetailWatch = useWatch({
    name: DentalHistoryChartFields.DentalProblemDetail,
    control,
  });

  return (
    <>
      <div className={styles.questions_wrapper}>
        <div className={styles.question_wrapper}>
          <div style={numberStyle}>1</div>
          <div className={styles.question_layout_symptom}>
            <p className="fs16 fw700">
              아래 나열한 구강증상 중에서{' '}
              <span className="text_underline">
                과거 또는 현재 경험하고 있는 증상
              </span>
              이 있다면 모두 체크해 주세요.
            </p>
            <div className="w100 mt24">
              <SymptomTable isRegister={isRegister && !isReadOnly} />
            </div>
          </div>
        </div>

        <div className={styles.question_wrapper}>
          <div style={numberStyle}>2</div>
          <div className={styles.question_layout}>
            <p className="fs16 fw700">
              가장 최근에 받은{' '}
              <span className="text_underline">
                스켈링 시기를 체크해주십시오.
              </span>
            </p>
            <div className="flex mt12">
              {LastScalingList.map((item) => (
                <FormRadioBtn
                  key={item.value}
                  name={DentalHistoryChartFields.LastScaling}
                  labelText={item.label}
                  value={item.value}
                  labelStyle={{ marginRight: '80px' }}
                  readOnly={!isRegister || isReadOnly}
                />
              ))}
            </div>
          </div>
        </div>

        <div className={styles.question_wrapper}>
          <div style={numberStyle}>3</div>
          <div className={styles.question_layout}>
            <p className="fs16 fw700">
              치위생 과정 중에 관심을 가져주길 바라는 부분을{' '}
              <span className="text_underline">모두</span> 체크해주십시오.
            </p>
            <div className="mt12">
              <FormCheckbox
                id={CheckPointLabel[CheckPoint.Check1]}
                name={DentalHistoryChartFields.CheckPoint}
                text={CheckPointLabel[CheckPoint.Check1]}
                value={CheckPoint.Check1}
                labelStyle={{ marginRight: '50px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={CheckPointLabel[CheckPoint.Check2]}
                name={DentalHistoryChartFields.CheckPoint}
                text={CheckPointLabel[CheckPoint.Check2]}
                value={CheckPoint.Check2}
                labelStyle={{ marginRight: '50px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={CheckPointLabel[CheckPoint.Check3]}
                name={DentalHistoryChartFields.CheckPoint}
                text={CheckPointLabel[CheckPoint.Check3]}
                value={CheckPoint.Check3}
                labelStyle={{ marginRight: '50px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={CheckPointLabel[CheckPoint.Check4]}
                name={DentalHistoryChartFields.CheckPoint}
                text={CheckPointLabel[CheckPoint.Check4]}
                value={CheckPoint.Check4}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <div className="flex mt12">
                <FormCheckbox
                  id={CheckPoint.Check5}
                  name={DentalHistoryChartFields.CheckPoint}
                  text={CheckPointLabel[CheckPoint.Check5]}
                  value={CheckPoint.Check5}
                  multiple={true}
                  readOnly={!isRegister || isReadOnly}
                  afterChange={() => {
                    if (
                      checkPointWatch?.includes(CheckPoint.Check5) &&
                      checkPointDetailWatch
                    ) {
                      openModal({
                        isConfirm: true,
                        title: '작성 중인 내용을 삭제하시겠습니까?',
                        text: '작성 중인 내용이 삭제됩니다.',
                        onCancel: () => {
                          setValue(DentalHistoryChartFields.CheckPoint, [
                            ...checkPointWatch,
                            CheckPoint.Check5,
                          ]);
                        },
                        onSubmit: () => {
                          setValue(
                            DentalHistoryChartFields.CheckPointDetail,
                            ''
                          );
                        },
                      });
                    }
                  }}
                />
                <FormAutoHeightTextarea
                  name={DentalHistoryChartFields.CheckPointDetail}
                  textareaStyle={{ flex: '1' }}
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    !checkPointWatch?.includes(CheckPoint.Check5)
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.question_wrapper}>
          <div style={numberStyle}>4</div>
          <div className={styles.question_layout}>
            <p className="fs16 fw700">
              과거에 치과 치료를 받았을 때 부정적인 경험이 있었습니까?
            </p>
            <div className="flex flex_column items_start gap12 mt12">
              <div className="flex w100">
                <FormRadioBtn
                  name={DentalHistoryChartFields.BadExperience}
                  value={DentalHistoryChartRadioBtn.Yes}
                  labelText={
                    DentalHistoryChartRadioBtnLabel[
                      DentalHistoryChartRadioBtn.Yes
                    ]
                  }
                  labelStyle={{ marginRight: '8px' }}
                  readOnly={!isRegister || isReadOnly}
                />
                <FormAutoHeightTextarea
                  name={DentalHistoryChartFields.BadExperienceDetail}
                  placeholder="해당 상황에 대해서 작성해 주세요."
                  textareaStyle={{ flex: '1' }}
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    badExperienceWatch !== DentalHistoryChartRadioBtn.Yes
                  }
                />
              </div>
              <FormRadioBtn
                name={DentalHistoryChartFields.BadExperience}
                value={DentalHistoryChartRadioBtn.No}
                labelText={
                  DentalHistoryChartRadioBtnLabel[DentalHistoryChartRadioBtn.No]
                }
                readOnly={!isRegister || isReadOnly}
                afterChange={() => {
                  if (badExperienceDetailWatch) {
                    openModal({
                      isConfirm: true,
                      title: '작성 중인 내용을 삭제하시겠습니까?',
                      text: '작성 중인 내용이 삭제됩니다.',
                      onCancel: () => {
                        setValue(
                          DentalHistoryChartFields.BadExperience,
                          DentalHistoryChartRadioBtn.Yes
                        );
                      },
                      onSubmit: () => {
                        setValue(
                          DentalHistoryChartFields.BadExperienceDetail,
                          ''
                        );
                      },
                    });
                  }
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.question_wrapper}>
          <div style={numberStyle}>5</div>
          <div className={styles.question_layout}>
            <p className="fs16 fw700">
              과거에 경험했던 치과 치료를{' '}
              <span className="text_underline">모두 체크</span>해 주십시오.
            </p>
            <div>
              <FormCheckbox
                id={DentalTreatmentLabel[DentalTreatment.Treatment1]}
                name={DentalHistoryChartFields.DentalTreatment}
                text={DentalTreatmentLabel[DentalTreatment.Treatment1]}
                value={DentalTreatment.Treatment1}
                labelStyle={{ marginRight: '23px' }}
                formCheckboxStyle={{ marginTop: '12px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalTreatmentLabel[DentalTreatment.Treatment2]}
                name={DentalHistoryChartFields.DentalTreatment}
                text={DentalTreatmentLabel[DentalTreatment.Treatment2]}
                value={DentalTreatment.Treatment2}
                labelStyle={{ marginRight: '23px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalTreatmentLabel[DentalTreatment.Treatment3]}
                name={DentalHistoryChartFields.DentalTreatment}
                text={DentalTreatmentLabel[DentalTreatment.Treatment3]}
                value={DentalTreatment.Treatment3}
                labelStyle={{ marginRight: '23px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalTreatmentLabel[DentalTreatment.Treatment4]}
                name={DentalHistoryChartFields.DentalTreatment}
                text={DentalTreatmentLabel[DentalTreatment.Treatment4]}
                value={DentalTreatment.Treatment4}
                labelStyle={{ marginRight: '23px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalTreatmentLabel[DentalTreatment.Treatment5]}
                name={DentalHistoryChartFields.DentalTreatment}
                text={DentalTreatmentLabel[DentalTreatment.Treatment5]}
                value={DentalTreatment.Treatment5}
                labelStyle={{ marginRight: '23px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalTreatmentLabel[DentalTreatment.Treatment6]}
                name={DentalHistoryChartFields.DentalTreatment}
                text={DentalTreatmentLabel[DentalTreatment.Treatment6]}
                value={DentalTreatment.Treatment6}
                labelStyle={{ marginRight: '23px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalTreatmentLabel[DentalTreatment.Treatment7]}
                name={DentalHistoryChartFields.DentalTreatment}
                text={DentalTreatmentLabel[DentalTreatment.Treatment7]}
                value={DentalTreatment.Treatment7}
                labelStyle={{ marginRight: '23px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalTreatmentLabel[DentalTreatment.Treatment8]}
                name={DentalHistoryChartFields.DentalTreatment}
                text={DentalTreatmentLabel[DentalTreatment.Treatment8]}
                value={DentalTreatment.Treatment8}
                labelStyle={{ marginRight: '23px' }}
                formCheckboxStyle={{ marginTop: '12px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalTreatmentLabel[DentalTreatment.Treatment9]}
                name={DentalHistoryChartFields.DentalTreatment}
                text={DentalTreatmentLabel[DentalTreatment.Treatment9]}
                value={DentalTreatment.Treatment9}
                labelStyle={{ marginRight: '23px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalTreatmentLabel[DentalTreatment.Treatment10]}
                name={DentalHistoryChartFields.DentalTreatment}
                text={DentalTreatmentLabel[DentalTreatment.Treatment10]}
                value={DentalTreatment.Treatment10}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />

              <div className="flex mt12">
                <FormCheckbox
                  id={DentalTreatmentLabel[DentalTreatment.Treatment11]}
                  name={DentalHistoryChartFields.DentalTreatment}
                  text={DentalTreatmentLabel[DentalTreatment.Treatment11]}
                  value={DentalTreatment.Treatment11}
                  labelStyle={{ marginRight: '0' }}
                  multiple={true}
                  readOnly={!isRegister || isReadOnly}
                  afterChange={() => {
                    if (
                      dentalTreatmentWatch?.includes(
                        DentalTreatment.Treatment11
                      ) &&
                      dentalTreatmentDetailWatch
                    ) {
                      openModal({
                        isConfirm: true,
                        title: '작성 중인 내용을 삭제하시겠습니까?',
                        text: '작성 중인 내용이 삭제됩니다.',
                        onCancel: () => {
                          setValue(DentalHistoryChartFields.DentalTreatment, [
                            ...dentalTreatmentWatch,
                            DentalTreatment.Treatment11,
                          ]);
                        },
                        onSubmit: () => {
                          setValue(
                            DentalHistoryChartFields.DentalTreatmentDetail,
                            ''
                          );
                        },
                      });
                    }
                  }}
                />
                <FormAutoHeightTextarea
                  name={DentalHistoryChartFields.DentalTreatmentDetail}
                  textareaStyle={{ flex: '1' }}
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    !dentalTreatmentWatch?.includes(DentalTreatment.Treatment11)
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <div className={styles.question_wrapper}>
          <div style={numberStyle}>6</div>
          <div className={styles.question_layout}>
            <p className="fs16 fw700">
              다음 중 고민한 적 있는 구강문제를{' '}
              <span className="text_underline">모두 체크</span>해 주십시오.
            </p>
            <div className="mt12">
              <FormCheckbox
                id={DentalProblemLabel[DentalProblem.Problem1]}
                name={DentalHistoryChartFields.DentalProblem}
                text={DentalProblemLabel[DentalProblem.Problem1]}
                value={DentalProblem.Problem1}
                labelStyle={{ marginRight: '23px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalProblemLabel[DentalProblem.Problem2]}
                name={DentalHistoryChartFields.DentalProblem}
                text={DentalProblemLabel[DentalProblem.Problem2]}
                value={DentalProblem.Problem2}
                labelStyle={{ marginRight: '23px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalProblemLabel[DentalProblem.Problem3]}
                name={DentalHistoryChartFields.DentalProblem}
                text={DentalProblemLabel[DentalProblem.Problem3]}
                value={DentalProblem.Problem3}
                labelStyle={{ marginRight: '23px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalProblemLabel[DentalProblem.Problem4]}
                name={DentalHistoryChartFields.DentalProblem}
                text={DentalProblemLabel[DentalProblem.Problem4]}
                value={DentalProblem.Problem4}
                labelStyle={{ marginRight: '23px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalProblemLabel[DentalProblem.Problem5]}
                name={DentalHistoryChartFields.DentalProblem}
                text={DentalProblemLabel[DentalProblem.Problem5]}
                value={DentalProblem.Problem5}
                labelStyle={{ marginRight: '23px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalProblemLabel[DentalProblem.Problem6]}
                name={DentalHistoryChartFields.DentalProblem}
                text={DentalProblemLabel[DentalProblem.Problem6]}
                value={DentalProblemLabel[DentalProblem.Problem6]}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <FormCheckbox
                id={DentalProblemLabel[DentalProblem.Problem7]}
                name={DentalHistoryChartFields.DentalProblem}
                text={DentalProblemLabel[DentalProblem.Problem7]}
                value={DentalProblem.Problem7}
                formCheckboxStyle={{ marginTop: '12px' }}
                multiple={true}
                readOnly={!isRegister || isReadOnly}
              />
              <div className="flex mt12">
                <FormCheckbox
                  id={DentalProblemLabel[DentalProblem.Problem8]}
                  name={DentalHistoryChartFields.DentalProblem}
                  text={DentalProblemLabel[DentalProblem.Problem8]}
                  value={DentalProblem.Problem8}
                  multiple={true}
                  readOnly={!isRegister || isReadOnly}
                  afterChange={() => {
                    if (
                      dentalProblemWatch?.includes(DentalProblem.Problem8) &&
                      dentalProblemDetailWatch
                    ) {
                      openModal({
                        isConfirm: true,
                        title: '작성 중인 내용을 삭제하시겠습니까?',
                        text: '작성 중인 내용이 삭제됩니다.',
                        onCancel: () => {
                          setValue(DentalHistoryChartFields.DentalProblem, [
                            ...dentalProblemWatch,
                            DentalProblem.Problem8,
                          ]);
                        },
                        onSubmit: () => {
                          setValue(
                            DentalHistoryChartFields.DentalProblemDetail,
                            ''
                          );
                        },
                      });
                    }
                  }}
                />
                <FormAutoHeightTextarea
                  name={DentalHistoryChartFields.DentalProblemDetail}
                  textareaStyle={{ flex: '1' }}
                  readOnly={
                    !isRegister ||
                    isReadOnly ||
                    !dentalProblemWatch?.includes(DentalProblem.Problem8)
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DentalHistoryChart;
